import React, { useEffect, useState } from 'react';
import { WifiOff, CloudUpload, AlertTriangle, RefreshCw, Trash2, X, CheckCircle2 } from 'lucide-react';
import { subscribe, processOutbox, retryItem, removeItem } from '../lib/offline';

const TYPE_LABEL = {
  psi_submit: 'Daily PSI', crew_clock_in: 'Crew clock-in', crew_clock_out: 'Crew clock-out',
  self_clock_in: 'My clock-in', self_clock_out: 'My clock-out',
};
const fmt = (iso) => new Date(iso).toLocaleString('en-CA', { timeZone: 'America/Toronto', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export const useOutbox = () => {
  const [snap, setSnap] = useState({ offline: false, items: [], pending: 0, failed: 0, processing: false });
  useEffect(() => subscribe(setSnap), []);
  return snap;
};

export const OfflineBar = () => {
  const snap = useOutbox();
  const [open, setOpen] = useState(false);
  const [justSynced, setJustSynced] = useState(null);
  useEffect(() => {
    const onSync = (e) => { setJustSynced(e.detail?.job?.label || 'Synced'); setTimeout(() => setJustSynced(null), 6000); };
    window.addEventListener('offline-synced', onSync);
    return () => window.removeEventListener('offline-synced', onSync);
  }, []);
  const show = snap.offline || snap.pending > 0 || snap.failed > 0 || justSynced;
  useEffect(() => { document.body.classList.toggle('has-offline-bar', !!show); return () => document.body.classList.remove('has-offline-bar'); }, [show]);
  if (!show) return null;
  const tone = snap.failed > 0 ? 'bg-red-700' : snap.offline ? 'bg-slate-800' : snap.pending > 0 ? 'bg-amber-600' : 'bg-emerald-700';
  const text = snap.failed > 0 ? `${snap.failed} didn't go through — tap to fix`
    : snap.offline ? (snap.pending > 0 ? `No signal — ${snap.pending} saved on this phone, will send when back in service` : 'No signal — working from this phone; anything you save is sent when back in service')
    : snap.pending > 0 ? `${snap.processing ? 'Sending' : 'Waiting to send'} ${snap.pending} item${snap.pending === 1 ? '' : 's'}…`
    : `Sent: ${justSynced}`;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="offline-bar"
        className={`fixed bottom-0 inset-x-0 z-[70] ${tone} text-white text-xs sm:text-sm px-3 py-2 flex items-center gap-2 shadow-lg text-left`}>
        {snap.failed > 0 ? <AlertTriangle className="w-4 h-4 shrink-0" /> : snap.offline ? <WifiOff className="w-4 h-4 shrink-0" /> : snap.pending > 0 ? <CloudUpload className={`w-4 h-4 shrink-0 ${snap.processing ? 'animate-pulse' : ''}`} /> : <CheckCircle2 className="w-4 h-4 shrink-0" />}
        <span className="flex-1 leading-tight">{text}</span>
        {(snap.pending > 0 || snap.failed > 0) && <span className="underline underline-offset-2 shrink-0">Outbox</span>}
      </button>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center sm:justify-center" onClick={() => setOpen(false)}>
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="offline-outbox">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div>
                <p className="font-bold text-slate-900">Outbox</p>
                <p className="text-xs text-slate-500">{snap.offline ? 'No signal right now. Items send on their own when the phone is back in service.' : 'Connected.'}</p>
              </div>
              <div className="flex items-center gap-2">
                {!snap.offline && snap.pending > 0 && (
                  <button type="button" onClick={processOutbox} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-blue-600 text-white flex items-center gap-1" data-testid="outbox-sync-now">
                    <RefreshCw className={`w-3.5 h-3.5 ${snap.processing ? 'animate-spin' : ''}`} /> Send now
                  </button>
                )}
                <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="outbox-close"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-3 space-y-2">
              {snap.items.length === 0 && <p className="text-sm text-slate-500 text-center py-6">Nothing waiting.</p>}
              {snap.items.map((it) => (
                <div key={it.id} className={`rounded-xl border p-3 ${it.status === 'failed' ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`} data-testid={`outbox-item-${it.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{TYPE_LABEL[it.type] || it.type}</p>
                      <p className="text-xs text-slate-600 break-words">{it.label}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">Saved {fmt(it.created_at)}{it.status === 'failed' ? ' · rejected on send' : ' · waiting'}</p>
                      {it.error && <p className="text-xs text-red-700 mt-1 font-medium" data-testid={`outbox-error-${it.id}`}>{it.error}</p>}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      {it.status === 'failed' && (
                        <button type="button" onClick={() => retryItem(it.id)} className="text-xs px-2 py-1 rounded-lg bg-white border border-slate-300 flex items-center gap-1" data-testid={`outbox-retry-${it.id}`}><RefreshCw className="w-3 h-3" /> Retry</button>
                      )}
                      <button type="button" onClick={() => { if (window.confirm('Throw this away? It will never be sent to Ops.')) removeItem(it.id); }}
                        className="text-xs px-2 py-1 rounded-lg bg-white border border-slate-300 text-red-700 flex items-center gap-1" data-testid={`outbox-discard-${it.id}`}><Trash2 className="w-3 h-3" /> Discard</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
